/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';

// import receptionRegister from '../../api/reception/receptionRegister';
import changeComplete from '../../api/reserveChange/changeComplete';
import changeConfirmation from '../../api/reserveChange/changeConfirmation';
import { clear } from '../../redux_toolkit/reserveChangeSlice';
import configMastaApi from '../../api/masta/configMasta';
import { useDidMount } from '../../customHooks';
import TopicPath from '../../components/TopicPath';
import { ReserveHeader } from '../../components/Header';
import { Footer } from '../../components/Footer';
import {
  RSV_FLAG_PHONE,
  INPUT_TYPE_NAME,
  INPUT_TYPE_ADDRESS,
  INPUT_TYPE_BIRTHDAY,
  INPUT_TYPE_PHONE,
  INPUT_TELLNUM_FLAG,
  INPUT_EXP_FLAG,
  INPUT_DATE_FLAG,
} from '../../constants';
import { dispTellStartPeople } from '../../utils';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

// 完了画面で表示しない個人情報系 inputType
const PERSONAL_INFO_TYPES = [
  INPUT_TYPE_NAME,
  INPUT_TYPE_ADDRESS,
  INPUT_TYPE_BIRTHDAY,
  INPUT_TYPE_PHONE,
  INPUT_TELLNUM_FLAG,
];

const ChangeComplete = function () {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const state = location?.state ?? '';
  const receptionId = state.receptionId ?? '';
  const rsvSetId = state.rsvSetId ?? '';
  const lgCd = state.lgCd ?? '';
  const businessChanged = state.businessChanged ?? false;

  const reserveChange = useSelector((state) => state.reserveChange);

  const handleError = useErrorHandler();

  const [checkedRecItems, setCheckedRecItems] = useState([]);
  const [business, setBusiness] = useState({});
  const [rsvNumber, setRsvNumber] = useState('');
  const [rsvDate, setRsvDate] = useState('');
  const [useMethod, setUseMethod] = useState('');
  // メールを送信した宛先メールアドレス
  const [mailAddress, setMailAddress] = useState('');
  // SMSを送信した宛先の携帯電話番号
  const [smsTelNum, setSmsTelNum] = useState('');
  const [fromMailAddress, setFromMailAddress] = useState('xxx@xxxxxx.ne.jp');
  const [mailSendTimes, setMailSendTimes] = useState('');
  const [smsSendTimes, setSmsSendTimes] = useState('');
  const [mailResendLimit, setMailResendLimit] = useState('');
  const [smsResendLimit, setSmsResendLimit] = useState('');
  // メールアドレス・電話番号の変更回数と上限（用件変更時の「変更して再送」で使用）
  const [mailUpdateTimes, setMailUpdateTimes] = useState('');
  const [tellUpdateTimes, setTellUpdateTimes] = useState('');
  const [mailUpdateLimit, setMailUpdateLimit] = useState('');
  const [tellUpdateLimit, setTellUpdateLimit] = useState('');
  // 「変更して再送」で入力する新しいメールアドレス・電話番号
  const [newMailAddress, setNewMailAddress] = useState('');
  const [newConfMailAddress, setNewConfMailAddress] = useState('');
  const [newTelNum, setNewTelNum] = useState('');
  const [contact, setContact] = useState({ mainName: '', mainTel: '', mainMail: '' });
  const [lgName, setLgName] = useState('');
  const [numOfAttendees, setNumOfAttendees] = useState(0);
  const [note, setNote] = useState('');
  const [sendType, setSendType] = useState(1);
  const [place, setPlace] = useState({ name: '', address: '', subAddress: '', tel: '' });
  const {
    name: placeName = '',
    address: placeAddress = '',
    subAddress: placeSubAddress = '',
    tel: placeTel = '',
  } = place;
  const [tellStart, setTellStart] = useState(1);
  const [rsvFlag, setRsvFlag] = useState(0);
  const [customizeData, setCustomizeData] = useState([]);
  const [businessChangeMode, setBusinessChangeMode] = useState(0);

  const infoMailRef = useRef(null);
  const infoSmsRef = useRef(null);

  const getRsvSetting = async (rsvSetId) => {
    const setting = await changeComplete.getRsvSetting(rsvSetId).catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    const {
      mailResendLimit = 0,
      smsResendLimit = 0,
      mailUpdateLimit = 0,
      tellUpdateLimit = 0,
      contact = '',
      lgName,
      tellStart = 1,
    } = setting;
    const { mainName = '', mainTel = '', mainMail = '' } = contact;

    setMailResendLimit(mailResendLimit);
    setSmsResendLimit(smsResendLimit);
    setMailUpdateLimit(mailUpdateLimit);
    setTellUpdateLimit(tellUpdateLimit);
    setContact({ mainName, mainTel, mainMail });
    setLgName(lgName);
    setTellStart(tellStart);
  };

  const getReception = async (receptionId) => {
    try {
      const getResult = await changeComplete.getReception(receptionId).catch((err) => {
        window.alert('受付データの取得に失敗しました。');
        handleError(err);
      });
      const {
        // lgCd = '',
        business = {},
        checkedRecItems = [],
        rsvNumber = '',
        rsvDate = '',
        useMethod = '',
        mailInfo = '',
        smsInfo = '',
        numOfAttendees = 0,
        note = '',
        sendType = 1,
        place,
        rsvFlag = 1,
        residentsInfo = [],
      } = getResult;

      // mailInfo からは送信先メールアドレス・送信回数・変更回数を取り出す
      const { mailAddress = '', mailSendTimes = '', mailUpdateTimes = '' } = mailInfo;
      // smsInfo からは送信先の携帯電話番号・送信回数・変更回数を取り出す（API のキー名は smsTellNum）
      const { smsTellNum = '', smsSendTimes = '', tellUpdateTimes = '' } = smsInfo;
      setBusiness(business);
      setCheckedRecItems(checkedRecItems);
      setRsvNumber(rsvNumber);
      setRsvDate(rsvDate);
      setUseMethod(useMethod);
      setMailAddress(mailAddress);
      setSmsTelNum(smsTellNum);
      setMailSendTimes(mailSendTimes);
      setSmsSendTimes(smsSendTimes);
      setMailUpdateTimes(mailUpdateTimes);
      setTellUpdateTimes(tellUpdateTimes);
      setNumOfAttendees(numOfAttendees);
      setNote(note);
      setSendType(sendType);
      setPlace(place);
      setRsvFlag(rsvFlag);
      setCustomizeData(residentsInfo);

      // 予約設定の取得
      getRsvSetting(rsvSetId);

      // 変更モードをDBから取得（Redux は既に clear されるため API 経由で取得）
      const modeSetting = await changeConfirmation.searchReserveSetting(rsvSetId, lgCd);
      setBusinessChangeMode(modeSetting?.businessChangeMode ?? 0);
    } catch {
      window.alert('受付情報の取得に失敗しました。');
      navigate(-1);
    }
  };

  const init = async () => {
    if (receptionId !== '') await getReception(receptionId);

    const fromMailConf = await configMastaApi.findByConfCd('rsv_from_mailaddress').catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    const { value: fromMail = '' } = fromMailConf;

    setFromMailAddress(fromMail);
  };

  useDidMount(() => {
    // ローカル state への値のコピーが完了しているためここで Redux をクリア
    dispatch(clear());
    init();
  });

  const displayRecItems = () => {
    const result = [];
    const recItems = checkedRecItems;
    const businessKeys = Object.keys(business);
    for (let i = 0; i < businessKeys.length; i += 1) {
      const nowBusinessKey = businessKeys[i];
      const { businessName = '' } = business[nowBusinessKey];
      const targetRecItems = recItems.filter(
        (item) => parseInt(item.businessId, 10) === parseInt(nowBusinessKey, 10)
      );

      const recItemKeys = Object.keys(targetRecItems);
      if (recItemKeys.length > 0) {
        result.push(<h3>{businessName}</h3>);
      }

      const tmpItems = {};
      for (let j = 0; j < recItemKeys.length; j += 1) {
        const nowRecItem = recItemKeys[j];
        const { groupId, groupName, recItemId, recItemName } = targetRecItems[nowRecItem];
        if (!tmpItems[groupId]) {
          tmpItems[groupId] = { groupName, items: [] };
        }

        tmpItems[groupId].items[recItemId] = <li style={{ marginLeft: '24px' }}>{recItemName}</li>;
      }

      const items = [];
      Object.keys(tmpItems).map((e) => {
        items.push(<strong>■{tmpItems[e].groupName}</strong>);
        items.push(<ul key={`item${i}`}>{tmpItems[e].items}</ul>);
      });

      result.push(items);
    }

    return result;
  };

  // ChangeForm で入力したフォーム回答を「変更内容」セクションごと返す（CustomizeComplete と同じ dl/dt/dd スタイル）
  const displayFormAnswers = () => {
    if (!customizeData || customizeData.length === 0) return null;

    const details = [];
    for (let i = 0; i < customizeData.length; i += 1) {
      const { question = '', answer = [], inputType = 1 } = customizeData[i];
      if (
        inputType !== INPUT_EXP_FLAG &&
        !(businessChangeMode === 2 && PERSONAL_INFO_TYPES.includes(inputType)) &&
        answer[0] !== '' &&
        answer.length !== 0
      ) {
        details.push(
          <dl key={`makeDetail_${i}`}>
            <dt className="textCnfCmp characterDown">{question}</dt>
            <dd className="textCnfCmp characterDown">
              {inputType === INPUT_DATE_FLAG || inputType === INPUT_TYPE_BIRTHDAY
                ? dayjs(answer[0]).format('YYYY[/]MM[/]DD')
                : answer.join(',')}
            </dd>
          </dl>
        );
      }
    }

    return details;
  };

  /**
   * SMS・メールをどこに送信したかを表示する。
   * @returns 送信したSMSの携帯電話番号・メールアドレス
   */
  const displaySendInfo = () => {
    const sendInfo = [];
    if (sendType === 2 || sendType === 3) {
      sendInfo.push(
        <dl key="SMS">
          <dt>SMS</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>送信する</dd>
          <dt>SMSを送信した携帯電話番号</dt>
          <dd>{smsTelNum}</dd>
        </dl>
      );
    }
    if (sendType === 1 || sendType === 3) {
      sendInfo.push(
        <dl key="mail">
          <dt>メール</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>{mailAddress}</dd>
        </dl>
      );
    }
    return sendInfo;
  };

  // メールの再送
  const onClickResendMail = async () => {
    if (mailSendTimes < mailResendLimit) {
      const { msg = '' } = await changeComplete.resendMail(receptionId);
      if (msg !== '') {
        window.alert(msg);
        return;
      }
      await getReception(receptionId);
      window.alert('メールを再送しました。');
    } else {
      window.alert(
        `メール再送可能制限に達しました。届かない場合はWEB窓口を利用できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  // SMSの再送
  const onClickResendSms = async () => {
    if (smsSendTimes < smsResendLimit) {
      const { msg = '' } = await changeComplete.resendSms(receptionId);
      if (msg !== '') {
        window.alert(msg);
        return;
      }
      await getReception(receptionId);
      window.alert('SMSを再送しました。');
    } else {
      window.alert(
        `SMS再送可能制限に達しました。届かない場合はWEB窓口を利用できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  // 新しいメールアドレスのバリデーション（CustomizeComplete と同等）
  const validationMail = () => {
    let message = '';

    // メールアドレスが区切り文字でつながっていないかを確認
    const separatorPattern = /[,:;、：；\s\u3000]/;
    if (separatorPattern.test(newMailAddress)) {
      message +=
        '複数のメールアドレスは入力できません。区切り文字（, や ;）を削除し、1件のみ入力してください。\n';
    }
    if (!newMailAddress.match(/\S/g)) {
      message += 'メールアドレスを入力してください。\n';
    } else if (newMailAddress.length > 256) {
      message += 'メールアドレスは256文字以内で入力してください。\n';
    } else {
      // @ または . がないと形式エラー
      const test = /^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/;
      if (
        !newMailAddress.includes('@') ||
        !newMailAddress.includes('.') ||
        !test.test(newMailAddress)
      ) {
        message += 'メールアドレスの形式が正しくありません。\n';
      }
    }

    if (!newConfMailAddress.match(/\S/g)) {
      message += 'メールアドレス(確認)を入力してください。\n';
    }

    if (
      newMailAddress.match(/\S/g) &&
      newConfMailAddress.match(/\S/g) &&
      newMailAddress !== newConfMailAddress
    ) {
      message += 'メールアドレスが確認用メールアドレスと異なります。\n';
    }

    if (message.length > 0) {
      window.alert(message);
      return false;
    }
    return true;
  };

  // 新しい携帯電話番号のバリデーション（CustomizeComplete と同等）
  const validationSms = () => {
    let message = '';

    const regax = /^0[789]0-?[0-9]{4}-?[0-9]{4}$/;
    if (!regax.test(newTelNum)) {
      message += 'SMSを利用する場合は携帯電話の番号を入力してください';
    }

    if (message.length > 0) {
      window.alert(message);
      return false;
    }
    return true;
  };

  // メールアドレスを変更して再送
  const onClickUpdateMail = async () => {
    if (validationMail() === false) return;

    if (mailUpdateTimes < mailUpdateLimit) {
      try {
        const { msg = '' } = await changeComplete.updateMail(receptionId, newMailAddress);
        if (msg !== '') {
          window.alert(msg);
          return;
        }
        await getReception(receptionId);
        setNewMailAddress('');
        setNewConfMailAddress('');
        window.alert('メールアドレスを変更して再送しました。');
      } catch (err) {
        window.alert('メールアドレスの変更に失敗しました。');
      }
    } else {
      window.alert(
        `メールアドレス変更可能制限に達しました。メールアドレスを変更できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  // 電話番号を変更して再送
  const onClickUpdateSms = async () => {
    if (validationSms() === false) return;

    if (tellUpdateTimes < tellUpdateLimit) {
      try {
        const { msg = '' } = await changeComplete.updateSms(receptionId, newTelNum);
        if (msg !== '') {
          window.alert(msg);
          return;
        }
        await getReception(receptionId);
        setNewTelNum('');
        window.alert('携帯電話番号を変更して再送しました。');
      } catch (err) {
        window.alert('電話番号の変更に失敗しました。');
      }
    } else {
      window.alert(
        `携帯電話番号変更可能制限に達しました。携帯電話番号を変更できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  /**
   *
   * @returns メールかSMSか両方か画面の文言が変わる
   */
  const chooseWordingMailOrSms = () => {
    const wordsComplete = [];
    if (sendType === 1 || sendType === 3) {
      wordsComplete.push(
        <p className="fs_14px txtCenter mb0">
          <a
            className="resendMailLink"
            role="button"
            tabIndex={0}
            onClick={() => {
              infoMailRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
              });
            }}
          >
            メールが届かない場合はこちらから再送してください。
          </a>
        </p>
      );
    }
    if (sendType === 2 || sendType === 3) {
      wordsComplete.push(
        <p className="fs_14px txtCenter mb40">
          <a
            className="resendSmsLink"
            role="button"
            tabIndex={0}
            onClick={() => {
              infoSmsRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
              });
            }}
          >
            SMSが届かない場合はこちらから再送してください。
          </a>
        </p>
      );
    }
    return wordsComplete;
  };

  /**
   *
   * @returns SMSもしくはメール、または両方の時の画面の文言変化
   */
  const resendSmsOrMail = () => {
    const resendSomething = [];
    if (sendType === 1 || sendType === 3) {
      resendSomething.push(
        <React.Fragment key="resend_mail">
          <div id="infoMail" className="inner450" ref={infoMailRef}>
            <h3 className="fs_15px txtCenter mb40">変更完了メールの受信を確認してください。</h3>
            <p className="txtLeft">
              メールが届かない場合は、{fromMailAddress}
              からの受信許可を確認いただき、再送ボタンを押してください。
            </p>
            <p className="txtCenter">
              <a
                role="button"
                // href="javascript:void(0);"
                className="btn btMail"
                onClick={() => onClickResendMail()}
                tabIndex={0}
              >
                メールを再送する
              </a>
            </p>
          </div>
          {/* 用件を変更した予約変更のときだけ、メールアドレスを変更して再送できる */}
          {businessChanged ? (
            <>
              <dl className="row wrap dlrowlist">
                <dt />
                <dd>
                  <p>メールアドレスを変更する場合は下記にて変更してください。</p>
                </dd>
                <dt>メール</dt>
                <dd>
                  <input
                    type="email"
                    name="newEmail"
                    style={{ width: '100%' }}
                    value={newMailAddress}
                    onChange={(e) => setNewMailAddress(e.target.value)}
                  />
                </dd>
                <dt>
                  メール&nbsp;<span>（確認用）</span>
                </dt>
                <dd>
                  <input
                    type="email"
                    name="newEmail2"
                    style={{ width: '100%' }}
                    value={newConfMailAddress}
                    onChange={(e) => setNewConfMailAddress(e.target.value)}
                  />
                </dd>
              </dl>
              <p className="mb40 txtCenter">
                <a
                  role="button"
                  className="btn btMail"
                  onClick={() => onClickUpdateMail()}
                  tabIndex={0}
                >
                  メールアドレスを変更して再送する
                </a>
              </p>
            </>
          ) : null}
        </React.Fragment>
      );
    }
    if (sendType === 2 || sendType === 3) {
      resendSomething.push(
        <React.Fragment key="resend_sms">
          <div id="infoSms" className="inner450" ref={infoSmsRef}>
            <h3 className="fs_15px txtCenter mb40">変更完了SMSの受信を確認してください。</h3>
            <p className="txtCenter mb0">SMSが届かない場合は、携帯電話でSMSの受信許可を</p>
            <p className="txtCenter">ご確認いただき、再送ボタンを押してください。</p>
            <p className="txtCenter">
              <a
                role="button"
                // href="javascript:void(0);"
                className="btn btMail"
                onClick={() => onClickResendSms()}
                tabIndex={0}
              >
                SMSを再送する
              </a>
            </p>
          </div>
          {/* 用件を変更した予約変更のときだけ、携帯電話番号を変更して再送できる */}
          {businessChanged ? (
            <>
              <dl className="row wrap dlrowlist">
                <dt />
                <dd>
                  <p>携帯電話番号を変更する場合は下記にて変更してください。</p>
                </dd>
                <dt>携帯電話番号</dt>
                <dd>
                  <input
                    type="tel"
                    name="newTelNumber"
                    style={{ width: '100%' }}
                    value={newTelNum}
                    onChange={(e) => setNewTelNum(e.target.value)}
                  />
                </dd>
              </dl>
              <p className="mb40 txtCenter">
                <a
                  role="button"
                  className="btn btMail"
                  onClick={() => onClickUpdateSms()}
                  tabIndex={0}
                >
                  携帯電話番号を変更して再送する
                </a>
              </p>
            </>
          ) : null}
        </React.Fragment>
      );
    }
    return resendSomething;
  };

  return (
    <>
      <ReserveHeader servCd="reserve" lgCd={reserveChange.lgCd} />
      <div id="container">
        <TopicPath />
        <main>
          <article>
            <div id="contents">
              <div className="inner">
                <h2>
                  予約変更が完了しました。
                  <br />
                  {sendType === 1 ? <>メールを送信しましたので確認してください。</> : null}
                  {sendType === 2 ? <>SMSを送信しましたので確認してください。</> : null}
                  {sendType === 3 ? <>メール、SMSを送信しましたので確認してください。</> : null}
                </h2>
                {chooseWordingMailOrSms()}
                <p className="txtCenter" style={{ fontSize: '20px' }}>
                  <strong>予約番号</strong>
                </p>
                <div id="reservation" className="mt0">
                  {rsvNumber}
                </div>
                <p className="txtCenter" style={{ fontSize: '20px' }}>
                  <strong>予約内容</strong>
                </p>
                <table className="tbl_confirmation noBorder mt0">
                  <tbody>
                    <tr>
                      <th>利用場所</th>
                      {useMethod ? (
                        <td>{useMethod}</td>
                      ) : (
                        <td>
                          {placeName}
                          {placeName && placeAddress ? <br /> : null}
                          {`${placeAddress} ${placeSubAddress}`}
                          {placeAddress && placeTel ? <br /> : null}
                          {placeTel ? `TEL: ${placeTel}` : ''}
                          <br />
                          {rsvFlag === RSV_FLAG_PHONE ? dispTellStartPeople(tellStart) : null}
                        </td>
                      )}
                    </tr>
                    <tr>
                      <th>利用日時</th>
                      <td>
                        {rsvDate
                          ? dayjs(rsvDate).format('YYYY[年]M[月]D[日](ddd) H[時]mm[分]')
                          : ''}
                      </td>
                    </tr>
                    {checkedRecItems.length > 0 ? (
                      <tr>
                        <th>用件</th>
                        <td>{displayRecItems()}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
                {businessChangeMode === 3 ||
                (businessChangeMode === 1 && !businessChanged) ? null : (
                  <>
                    <p className="txtCenter">
                      <strong>入力内容</strong>
                    </p>
                    <div className="customInputData">
                      {/* ChangeForm で入力したフォーム回答（変更内容） */}
                      {displayFormAnswers()}
                      {numOfAttendees > 0 ? (
                        <dl>
                          <dt>同席者数</dt>
                          <dd>{numOfAttendees}人</dd>
                        </dl>
                      ) : null}
                      {note !== '' ? (
                        <dl>
                          <dt>備考</dt>
                          <dd
                            style={{ overflowWrap: 'anywhere' }}
                            className="textDown characterDown"
                          >
                            {note}
                          </dd>
                        </dl>
                      ) : null}
                      {/* メール・SMSをどこに送信したか（CustomizeComplete と同じ位置に表示） */}
                      {displaySendInfo()}
                      {rsvFlag === 0 ? (
                        <div style={{ margin: '30px 0', textAlign: 'center', fontSize: '14px' }}>
                          変更完了通知に記載されているURLは、利用日時の約1週間後まで有効です
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
                <hr />
                {resendSmsOrMail()}
                <p className="txtCenter">
                  <strong style={{ fontSize: '20px' }}>
                    予約を変更しました。
                    <br />
                    ブラウザを閉じてください。
                  </strong>
                </p>
              </div>
            </div>
          </article>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default ChangeComplete;
